import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import api from "../../api";
import "./TicketExchangeModal.css";

const TicketExchangeModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  const [items, setItems] = useState([]);
  const [userTickets, setUserTickets] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [purchaseQuantities, setPurchaseQuantities] = useState({});
  
  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Categories for filtering
  const categories = [
    { id: "all", name: "All Items" },
    { id: "catching", name: "Catching" },
    { id: "mining", name: "Mining" },
    { id: "battling", name: "Battling" },
    { id: "tms", name: "TMs" },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await api.get("/v1/ticket-exchange/items", {
        withCredentials: true
      });
      
      // Sort items by price (least to most expensive)
      const sortedItems = [...response.data.items].sort((a, b) => a.ticket_cost - b.ticket_cost);
      
      setItems(sortedItems);
      setUserTickets(response.data.user_tickets);
      
      // Initialize purchase quantities
      const quantities = {};
      sortedItems.forEach(item => {
        quantities[item.id] = 1;
      });
      setPurchaseQuantities(quantities);
      
      setLoading(false);
    } catch (err) {

      setLoading(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    const quantity = Math.max(1, Math.min(99, parseInt(value) || 1));
    setPurchaseQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  const handlePurchase = async (item) => {
    try {
      const quantity = purchaseQuantities[item.id] || 1;
      const response = await api.post(
        "/v1/ticket-exchange/purchase",
        {
          item_id: item.id,
          quantity: quantity
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Update user's tickets
        setUserTickets(response.data.remaining_tickets);
        
        // Show success message
        spawnText(
          response.data.message,
          playerPos.x,
          playerPos.y - 20,
          "#4CAF50"
        );
        
        // Refresh items to update stock
        fetchItems();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Error purchasing item.";
      spawnText(errorMessage, playerPos.x, playerPos.y - 20, "#f44336");
    }
  };

  const getFilteredItems = () => {
    if (activeCategory === "all") {
      return items;
    }
    return items.filter(item => item.category === activeCategory);
  };

  const canAfford = (item, quantity) => {
    return userTickets >= (item.ticket_cost * quantity);
  };

  const isInStock = (item, quantity) => {
    return item.stock === -1 || item.stock >= quantity;
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="ticket-exchange-modal" size="lg">
      <ModalHeader toggle={toggle}>
        <div className="d-flex justify-content-between align-items-center w-100">
       
          <div className="ticket-balance">
            <img src="items/Ticket.png" alt="Ticket" style={{ height: '24px', marginRight: '8px' }} />
            <span>{formatNumber(userTickets)}</span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}
        
        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Category filters */}
            <div className="category-filters mb-4">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            {/* Items grid */}
            <div className="items-grid">
              {getFilteredItems().map(item => {
                const quantity = purchaseQuantities[item.id] || 1;
                const totalCost = item.ticket_cost * quantity;
                const canBuy = canAfford(item, quantity) && isInStock(item, quantity);
                
                return (
                  <div key={item.id} className="item-card">
                    <div className="item-image">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="item-details">
                      <h5>{item.name}</h5>
                      <p className="item-description">{item.description}</p>
                      <div className="item-cost">
                        <img src="items/Ticket.png" alt="Ticket" style={{ height: '18px', marginRight: '5px' }} />
                        <span>{formatNumber(item.ticket_cost)} per item</span>
                      </div>
                      {item.stock !== -1 && (
                        <div className="item-stock">
                          <span>In stock: {formatNumber(item.stock)}</span>
                        </div>
                      )}
                      <div className="purchase-controls">
                        <div className="quantity-control">
                          <label>Qty:</label>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={quantity}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          />
                        </div>
                        <div className="purchase-total">
                        <img src="items/Ticket.png" alt="Ticket" style={{ height: '18px', marginRight: '5px' }} />
                        <span>{formatNumber(totalCost)}</span>
                      </div>
                        <button
                          className={`buy-btn ${!canBuy ? 'disabled' : ''}`}
                          onClick={() => handlePurchase(item)}
                          disabled={!canBuy}
                        >
                          {!canBuy ? (
                            isInStock(item, quantity) ? "Can't Afford" : "Out of Stock"
                          ) : (
                            "Purchase"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default TicketExchangeModal;
